/**
 * ContactDetailsDrawer
 *
 * Painel "Dados do contato" estilo WhatsApp: desliza da direita, mostra avatar
 * grande centralizado, nome editavel inline com icone de lapiz, telefone, e
 * listas inline de feedbacks (ContactFeedbackService) e followups
 * (followupService.getByRecipient) cadastrados para o contato.
 *
 * Funciona tanto com WA Business quanto com WA Personal — a prop `source`
 * decide qual endpoint chamar para salvar a edicao de nome.
 *
 * Props:
 *   - isOpen, onClose
 *   - colors                 -> theme (useTheme)
 *   - contactPhone           -> telefone (com ou sem formatacao)
 *   - contactName            -> nome atual exibido
 *   - contactProfilePicUrl   -> URL do avatar (opcional)
 *   - source                 -> 'wa_business' | 'wa_personal'
 *   - chatId                 -> obrigatorio quando source==='wa_business'
 *   - onNameUpdated(newName) -> callback apos sucesso da edicao
 *   - onNavigateToFollowup(id)
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X, User, Users, Edit2, Loader2, Trash2, Calendar, Repeat, Clock,
  ExternalLink, MessageSquarePlus, AlertCircle, Phone, Check, Tag,
  UserCheck, Bot, ChevronDown, ChevronUp, GitBranch, SlidersHorizontal,
} from 'lucide-react';
import waBusinessService from '../../services/wa-business/waBusinessService';
import whatsappService from '../../services/wa-personal/whatsappService';
import contactFeedbackService from '../../services/global/contactFeedbackService';
import followupService from '../../services/global/followupService';
import contactTagsService from '../../services/global/contactTagsService';
import assignmentService from '../../services/global/assignmentService';
import companyContactService from '../../services/global/companyContactService';
import contactJourneyService from '../../services/global/contactJourneyService';
import calendarService from '../../services/global/calendarService';
import CustomFieldsPanel from './CustomFieldsPanel';
import ContactTagsManagerModal from './ContactTagsManagerModal';
import TagCombobox from './TagCombobox';
import LabelPickerModal from '../labels/LabelPickerModal';
import LinkNumbersModal from './LinkNumbersModal';
import leadsService from '../../services/global/leadsService';
import { formatPhoneNumber } from '../../utils/wa-personal/chat_utils';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import useContactLabelsCount from '../../hooks/useContactLabelsCount';
import authService from '../../services/global/authService';
import { missingRequired } from './requiredFieldsRule';
import { useAuth } from '../../contexts/AuthContext';
import BlocklistToggle from '../campaigns/BlocklistToggle';
import { normalizeCreatePhone } from './createPhoneRule';
import { linkedContactLabel, linkedContactTarget } from './linkedContacts';
import ContactChannelChips from './ContactChannelChips';
import LinkChannelModal from './LinkChannelModal';

const RECURRENCE_LABELS = {
  once: 'Envio único',
  daily: 'Diário',
  weekly: 'Semanal',
  monthly: 'Mensal',
};

const WEEKDAY_NAMES = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

// Origem dos eventos de funil/etapa (mesmo mapa do ContactJourneyLogs,
// nomenclatura funil/etapa da leva do inbox).
const FUNNEL_EVENT_SOURCES = {
  kanban: 'arrastar e soltar',
  modal: 'seleção manual',
  rule: 'regra de aproximação',
  auto_dispatch: 'auto-disparo',
  api: 'integração',
  agent: 'agente IA',
  csv_import: 'importação',
  label_dispatch: 'disparo de etapa',
  column_deleted: 'etapa/funil excluído',
};

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

const formatDateBR = (dateStr) => {
  if (!dateStr || dateStr.length !== 10) return dateStr || '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const buildScheduleText = (schedule) => {
  if (!schedule) return '';
  const time = schedule.send_time || '09:00';
  const rec = schedule.recurrence || 'once';
  if (rec === 'once') {
    const dateStr = formatDateBR(schedule.send_date || schedule.start_date || '');
    return `Uma vez às ${time}${dateStr ? ` em ${dateStr}` : ''}`;
  }
  if (rec === 'daily') return `Diário às ${time}`;
  if (rec === 'weekly') {
    const days = (schedule.recurrence_days || []).map(d => WEEKDAY_NAMES[d] || '').filter(Boolean);
    return `Semanal ${days.join('/')} às ${time}`;
  }
  if (rec === 'monthly') {
    const day = schedule.recurrence_day_of_month || 1;
    return `Mensal dia ${day} às ${time}`;
  }
  return `${RECURRENCE_LABELS[rec] || rec} às ${time}`;
};

const formatFeedbackDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

// P5 — rotulo de uma identidade do lead (channel-agnostic). WhatsApp -> numero
// formatado; Instagram/Messenger (futuro) -> "Instagram @handle". Base pronta
// para o Instagram Direct entrar sem retrabalho.
const formatIdentity = (i) => {
  const ref = String((i && i.ref) || '');
  const ch = String((i && i.channel) || '');
  const ext = (i && i.external_id) || '';
  if (ref.startsWith('wa:') || ch.startsWith('wa') || ch === 'whatsapp') {
    return formatPhoneNumber(ext) || ext;
  }
  if (ref.startsWith('instagram:') || ch === 'instagram') return `Instagram ${ext}`;
  if (ref.startsWith('messenger:') || ch === 'messenger') return `Messenger ${ext}`;
  if (ref.startsWith('widget:') || ch === 'widget') return 'Widget do site';
  return ext;
};

// Seção colapsável padronizada do drawer (cabeçalho com ícone + título +
// contagem opcional + chevron; corpo recolhível). resetKey re-sincroniza o
// aberto/fechado ao trocar de contato.
const CollapsibleSection = ({ title, icon, count = 0, colors, defaultOpen = true, resetKey, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { setOpen(defaultOpen); }, [resetKey, defaultOpen]);
  return (
    <section style={{ padding: '14px 20px', borderBottom: `8px solid ${colors.bgSecondary}` }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', background: 'transparent', border: 'none',
          cursor: 'pointer', padding: 0,
        }}
      >
        <span style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 14, fontWeight: 600, color: colors.textPrimary,
        }}>
          {icon}
          {title}
          {count > 0 && (
            <span style={{
              fontSize: 11, color: colors.textSecondary,
              padding: '2px 8px', borderRadius: 10, backgroundColor: colors.bgSecondary,
            }}>
              {count}
            </span>
          )}
        </span>
        {open
          ? <ChevronUp size={16} color={colors.textTertiary} />
          : <ChevronDown size={16} color={colors.textTertiary} />}
      </button>
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </section>
  );
};

const ContactDetailsDrawer = ({
  isOpen,
  onClose,
  colors,
  contactPhone,
  // Arquitetura empresarial (fase 3, secao 7): as MESMAS tres props nos 4
  // shells. `contactKey` e a chave canonica (telefone ou chave opaca) e
  // substitui `contactPhone` quando vier; `companyContactId` e o `cc:` da
  // empresa (nulo em conta sem empresa ou antes do backfill); `channel` e o
  // canal real. Com features.company_contact_ids desligada nada muda.
  contactKey = null,
  companyContactId = null,
  channel = null,
  contactName,
  contactProfilePicUrl,
  // Instagram: legenda opcional (o @handle) mostrada no lugar do telefone, que
  // o contato de IG nao tem. O contact_ref ('instagram:{igsid}') chega em
  // contactPhone e viaja opaco para os serviços de CRM (ja channel-aware).
  contactSubtitle,
  source = 'wa_business',
  chatId,
  onNameUpdated,
  onNavigateToFollowup,
  // Modo "criar contato" (botão + da lista): telefone editável; mostra só
  // tags/comentários/campos (esconde followups e "Mais informações"); botão
  // "Criar contato" -> onCreate(phone, name), que abre a tela de mensagem.
  createMode = false,
  onCreate,
  // Chamado após EXCLUIR o contato (purga). O pai remove o chat da lista/fecha.
  onDeleted,
  // Chamado após EDITAR o número (re-key). O pai reabre no novo número/atualiza.
  onRekeyed,
}) => {
  const { isCompanyAdmin, user } = useAuth();
  const { flags: featureFlags } = useFeatureFlags();
  const navigate = useNavigate();
  // Canal efetivo: `channel` (novo) cai no `source` quando nao informado. A
  // chave efetiva (contactKey vence contactPhone) entra nas chamadas por tela
  // na proxima entrega; nesta, as chamadas continuam iguais.
  const effectiveChannel = channel || source;
  // Contato empresarial em um documento (GET /api/company-contacts/{id}):
  // so com a flag e o id; hoje alimenta apenas o modo shadow (console.debug),
  // as chamadas por tela continuam iguais nesta entrega.
  useEffect(() => {
    if (!isOpen || !companyContactId || !featureFlags?.company_contact_ids) return undefined;
    let alive = true;
    companyContactService.get(companyContactId)
      .then((doc) => {
        if (!alive || !doc) return;
        if (featureFlags?.company_contact_shadow) {
          // eslint-disable-next-line no-console
          console.debug('[cc-shadow] drawer', { id: doc.id, version: doc.version, channel: effectiveChannel });
        }
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyContactId, featureFlags?.company_contact_ids]);
  // Instagram/Messenger: sem telefone. O CRM (tag/etiqueta/campo/responsável/
  // feedback) funciona opaco pela chave 'instagram:{igsid}' ou
  // 'messenger:{psid}', mas as afordâncias telefone-cêntricas (editar número,
  // vincular números, excluir por telefone) e a edição de nome (o nome vem do
  // perfil da Meta, sem endpoint de edição) ficam de fora. O Messenger reusa o
  // MESMO braço do IG — as restrições são idênticas.
  const isInstagram = source === 'instagram'
    || String(contactPhone || '').startsWith('instagram:')
    || source === 'messenger'
    || String(contactPhone || '').startsWith('messenger:')
    // Widget do site: mesma familia opaca (sem telefone, nome vem do visitante).
    || source === 'widget'
    || String(contactPhone || '').startsWith('widget:');
  const [showLinkNumbers, setShowLinkNumbers] = useState(false);
  // Lead unificado nos 3 canais (flag lead_multichannel): modal "Vincular a
  // outro canal" e contador que recarrega os vinculos depois de unificar.
  const [showLinkChannel, setShowLinkChannel] = useState(false);
  const [linkedReload, setLinkedReload] = useState(0);
  const [leadOthers, setLeadOthers] = useState([]); // outras identidades do mesmo lead (P5)
  // Contatos de OUTROS canais do mesmo lead (gravados pelo cadastro do agente:
  // Instagram/Messenger <-> WhatsApp). Carrega junto com o responsável.
  const [linkedContacts, setLinkedContacts] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [showRekeyConfirm, setShowRekeyConfirm] = useState(false);
  // Edicao do nome
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [currentName, setCurrentName] = useState(contactName || '');

  // Feedbacks
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  // Followups
  const [followups, setFollowups] = useState([]);
  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [removingFollowupId, setRemovingFollowupId] = useState(null);

  // Tags (múltiplas por contato, criáveis na hora)
  const [tags, setTags] = useState([]);
  const [showTagsModal, setShowTagsModal] = useState(false);

  // Seção "Responsável" — responsável atual + origem + histórico de
  // transferências. Carregado junto com o drawer (a seção abre por padrão).
  const [assignmentInfo, setAssignmentInfo] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [loadingAssignment, setLoadingAssignment] = useState(false);
  const [originInfo, setOriginInfo] = useState(null);  // origem de entrada: por qual número/membro o lead chegou
  const [historyVisible, setHistoryVisible] = useState(2);  // paginação 2-a-2 do histórico de transferências

  // Auditoria pedida pelo cliente: histórico de funil/etapa (eventos que o
  // backend JÁ grava em contact_label_events) + agendamentos do contato
  // (eventos nativos do calendário). Mesmo padrão do histórico de
  // responsável: paginado 2 a 2.
  const [funnelHistory, setFunnelHistory] = useState([]);
  const [funnelHistVisible, setFunnelHistVisible] = useState(2);
  const [contactEvents, setContactEvents] = useState([]);
  const [eventsVisible, setEventsVisible] = useState(2);

  // Último fechamento (ganho/perda) do contato, com o motivo que o operador
  // digitou. Derivado do histórico JÁ carregado — o valor durável vive em
  // contact.outcome_reasons[funil], mas ler dali exigiria um fetch novo e o
  // histórico (ordenado do mais recente) responde a mesma pergunta.
  const lastOutcome = useMemo(
    () => (funnelHistory || []).find(
      (ev) => ev.outcome === 'won' || ev.outcome === 'lost'
    ) || null,
    [funnelHistory]
  );

  // Criar contato: telefone digitado no próprio drawer. Em modo normal,
  // effectivePhone === contactPhone (comportamento de visualização intacto).
  const [createPhone, setCreatePhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [createNameError, setCreateNameError] = useState('');   // nome so com telefone (03/09)
  // Criar-lead: checagem de duplicado + etiqueta + responsável automático.
  const [dupInfo, setDupInfo] = useState(null);          // { exists, contact, responsible }
  const [forceCreate, setForceCreate] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  // Mesma chave no handleCreate, no aviso de duplicado e no que for escolhido
  // ANTES de salvar (etapa/tag/campo). Sem isto, a etapa ia para
  // "labels_only:34..." enquanto o assignSelf gravava em "labels_only:5534...":
  // DOIS stubs órfãos para o mesmo lead (achado N3 da auditoria fonte-única,
  // 2026-08-19). A regra espelha o envio do backend (to_send_digits): com "+"
  // vale como veio; sem "+", DDD + número ganha 55, menos o número +1 que não
  // forma telefone brasileiro (levantamento myCar 13/09).
  const effectivePhone = createMode ? normalizeCreatePhone(createPhone) : contactPhone;
  // Funil/etapa ATUAL do contato — alimenta a tag da seção "Funil / etapa".
  // No createMode o hook liga quando o telefone está COMPLETO (>=10 dígitos)
  // e ESTÁVEL por 500ms: sem o debounce, cada dígito após o 10º re-disparava
  // clear+fetch (pisca) e podia exibir por instantes o funil de OUTRO lead
  // cujo número é prefixo do digitado.
  const [stableCreatePhone, setStableCreatePhone] = useState(null);
  useEffect(() => {
    if (!createMode) return undefined;
    const next = effectivePhone.length >= 10 ? effectivePhone : null;
    const t = setTimeout(() => setStableCreatePhone(next), 500);
    return () => clearTimeout(t);
  }, [createMode, effectivePhone]);
  // So busca com o drawer ABERTO: os hooks rodam antes do `if (!isOpen)`
  // abaixo, e o drawer fechado (montado pelos 4 ChatWindows) pedia
  // GET /contacts/{chave}/labels junto com o cabecalho a cada conversa aberta.
  const contactFunnel = useContactLabelsCount(
    isOpen ? (createMode ? stableCreatePhone : contactPhone) : null
  );
  // Multi-funil (flag multi_funnel, 2026-09-16): a seção "Funil / etapa"
  // mostra uma etapa POR FUNIL, com "Sair deste funil" em cada uma e o botão
  // "Entrar em outro funil". Sem a flag, a tag única de sempre.
  const multiFunnel = Boolean(featureFlags?.multi_funnel);
  const [leavingFunnelLabelId, setLeavingFunnelLabelId] = useState(null);
  const handleLeaveFunnel = useCallback(async (labelId) => {
    if (!effectivePhone || !labelId || leavingFunnelLabelId) return;
    setLeavingFunnelLabelId(labelId);
    try {
      await authService.removeLabelFromContact(effectivePhone, labelId);
      contactFunnel.reload();
    } catch (err) {
      console.error('[ContactDetailsDrawer] sair do funil:', err);
    } finally {
      setLeavingFunnelLabelId(null);
    }
  }, [effectivePhone, leavingFunnelLabelId, contactFunnel]);
  // Habilita escolher etapa: no createMode exige telefone completo (mesmo
  // gate do "Criar contato") — antes 1 dígito bastava e a etiqueta ia parar
  // num telefone parcial (stub órfão no banco).
  const canPickFunnel = createMode ? effectivePhone.length >= 10 : !!effectivePhone;

  // Levantamento myCar 13/09 (decisão 1.2, flag required_fields_popup):
  // "Criar contato" só salva com os obrigatórios preenchidos. Mesma regra do
  // /required (etapa em required_stages OU funil em required_pipelines). Com
  // etapa escolhida vale a etapa; sem etapa, vale o funil PADRÃO, que é onde o
  // lead sem etapa aparece. Valores e definições vêm do painel de campos.
  const requireOnCreate = createMode && Boolean(featureFlags?.required_fields_popup);
  const [createFields, setCreateFields] = useState({ values: {}, definitions: [] });
  const [defaultFunnelId, setDefaultFunnelId] = useState(null);
  useEffect(() => {
    if (!isOpen || !requireOnCreate) return undefined;
    let alive = true;
    authService.listFunnels()
      .then((list) => {
        if (!alive) return;
        const arr = Array.isArray(list) ? list : [];
        const def = arr.find((f) => f.is_default) || arr[0] || null;
        setDefaultFunnelId(def ? def.id : null);
      })
      .catch(() => { if (alive) setDefaultFunnelId(null); });
    return () => { alive = false; };
  }, [isOpen, requireOnCreate]);
  const createMissing = useMemo(() => {
    if (!requireOnCreate) return [];
    const stage = contactFunnel.label;
    const scope = stage
      ? { stageId: stage.id, pipelineId: stage.pipeline_id }
      : { stageId: null, pipelineId: defaultFunnelId };
    return missingRequired(createFields.definitions, createFields.values, scope);
  }, [requireOnCreate, contactFunnel.label, defaultFunnelId, createFields]);

  // P5 — "Mesma pessoa": busca as OUTRAS identidades do lead (read-only, nao
  // cria). Refaz quando o modal de vincular fecha (dep showLinkNumbers).
  // Conversas seguem SEPARADAS — isto e so referencia no cartao.
  useEffect(() => {
    if (!isOpen || createMode || !featureFlags.lead_model || !contactPhone) {
      setLeadOthers([]);
      return undefined;
    }
    let cancelled = false;
    leadsService
      .search(contactPhone, source)
      .then((res) => {
        if (cancelled) return;
        const ids = (res && res.data && res.data.identities) || [];
        const cur = onlyDigits(contactPhone);
        setLeadOthers(ids.filter((i) => onlyDigits(i.external_id) !== cur));
      })
      .catch(() => { if (!cancelled) setLeadOthers([]); });
    return () => { cancelled = true; };
  }, [isOpen, createMode, featureFlags.lead_model, contactPhone, source, showLinkNumbers]);

  const loadTags = useCallback(async () => {
    setTags([]);  // limpa o do contato anterior (a contagem no cabeçalho vazava)
    if (!contactPhone) return;
    try {
      setTags(await contactTagsService.getContactTags(contactPhone));
    } catch (err) {
      console.error('[ContactDetailsDrawer] tags load error:', err);
    }
  }, [contactPhone]);

  // Se o "nome" exibido é na verdade o número (contato sem nome real), o draft
  // de edição começa VAZIO — assim o placeholder "Nome do contato" aparece e o
  // usuário não acha que está editando o telefone.
  const nameIsPhone = (n) =>
    onlyDigits(contactPhone).length > 0 && onlyDigits(n) === onlyDigits(contactPhone);

  useEffect(() => {
    setCurrentName(contactName || '');
    setEditing(false);
    setNameDraft(nameIsPhone(contactName) ? '' : (contactName || ''));
    setNameError(null);
    if (createMode) {
      setCreatePhone('');
      // Reabrir o drawer de criação não pode herdar o estado da tentativa
      // anterior (aviso de duplicado com telefone vazio, forçar-criação).
      setDupInfo(null);
      setForceCreate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactName, contactPhone, isOpen]);

  const loadFeedbacks = useCallback(async () => {
    setFeedbacks([]);  // limpa comentários do contato anterior (vazavam em erro)
    setFeedbackError(null);
    if (!contactPhone) return;
    setLoadingFeedbacks(true);
    try {
      const list = await contactFeedbackService.listFeedbacks(contactPhone);
      setFeedbacks(list || []);
    } catch (err) {
      console.error('[ContactDetailsDrawer] feedback load error:', err);
      setFeedbackError('Não foi possível carregar os comentários.');
    } finally {
      setLoadingFeedbacks(false);
    }
  }, [contactPhone]);

  const loadFollowups = useCallback(async () => {
    if (!contactPhone) return;
    setLoadingFollowups(true);
    try {
      const res = await followupService.getByRecipient(contactPhone);
      setFollowups(res?.data || []);
    } catch (err) {
      console.error('[ContactDetailsDrawer] followup load error:', err);
      setFollowups([]);
    } finally {
      setLoadingFollowups(false);
    }
  }, [contactPhone]);

  // Responsável atual + histórico + origem de entrada. Carrega junto com o
  // drawer (a seção "Responsável" agora abre por padrão, não mais no toggle).
  // Guarda de geração: descarta resposta obsoleta ao trocar de contato e LIMPA
  // o estado do contato anterior de imediato (senão o responsável/histórico de
  // A vazaria pro contato B enquanto o fetch de B não volta — ou se ele falhar).
  const assignReqRef = useRef(0);
  const loadAssignment = useCallback(async () => {
    setAssignmentInfo(null);
    setAssignmentHistory([]);
    setOriginInfo(null);
    setFunnelHistory([]);
    setContactEvents([]);
    setLinkedContacts([]);
    if (!contactPhone) return;
    const seq = ++assignReqRef.current;
    setLoadingAssignment(true);
    try {
      const [cur, hist, origin, journey, events, linked] = await Promise.all([
        assignmentService.get(contactPhone),
        assignmentService.getHistory(contactPhone, 50),
        assignmentService.getEntryOrigin(contactPhone).catch(() => null),
        contactJourneyService.getLabelHistory(contactPhone, 50).catch(() => null),
        calendarService.listEvents({ contactPhone }).catch(() => []),
        assignmentService.getLinkedContacts(contactPhone).catch(() => null),
      ]);
      if (seq !== assignReqRef.current) return;  // trocou de contato no meio
      setAssignmentInfo(cur?.data?.assigned_to || null);
      setAssignmentHistory(hist?.data?.events || []);
      setOriginInfo(origin?.data || null);
      setFunnelHistory(journey?.data?.events || []);
      setLinkedContacts(Array.isArray(linked?.data) ? linked.data : []);
      // Agendamentos: mais recentes primeiro (o endpoint devolve do período).
      const evs = Array.isArray(events) ? [...events] : [];
      evs.sort((a, b) => String(b.start_at || '').localeCompare(String(a.start_at || '')));
      setContactEvents(evs);
    } catch (err) {
      console.warn('[ContactDetailsDrawer] assignment load:', err);
    } finally {
      if (seq === assignReqRef.current) setLoadingAssignment(false);
    }
  }, [contactPhone]);

  useEffect(() => {
    if (isOpen) {
      loadFeedbacks();
      loadFollowups();
      loadTags();
      setFeedbackText('');
      setHistoryVisible(2);
      setFunnelHistVisible(2);
      setEventsVisible(2);
      if (!createMode) loadAssignment();
    }
  }, [isOpen, createMode, loadFeedbacks, loadFollowups, loadTags, loadAssignment]);

  if (!isOpen) return null;

  const displayName = currentName || contactPhone || 'Contato';

  const handleSaveName = async () => {
    const trimmed = (nameDraft || '').trim();
    if (!trimmed) {
      setNameError('O nome não pode ficar em branco.');
      return;
    }
    if (trimmed === (currentName || '').trim()) {
      setEditing(false);
      return;
    }
    // Em modo criar, o nome é só local (ainda não há contato no backend).
    if (createMode) {
      setCurrentName(trimmed);
      setEditing(false);
      return;
    }
    // Instagram nao tem endpoint de edicao de nome — o nome vem do perfil do IG.
    if (isInstagram) {
      setEditing(false);
      return;
    }
    setSavingName(true);
    setNameError(null);
    try {
      if (source === 'wa_business') {
        if (!chatId) throw new Error('chatId ausente para WA Business');
        await waBusinessService.updateChatContactName(chatId, trimmed);
      } else {
        await whatsappService.updateContactPushName(contactPhone, trimmed);
      }
      setCurrentName(trimmed);
      setEditing(false);
      onNameUpdated?.(trimmed);
    } catch (err) {
      console.error('[ContactDetailsDrawer] save name error:', err);
      setNameError(
        err?.response?.data?.detail ||
        err.message ||
        'Erro ao salvar o nome.'
      );
    } finally {
      setSavingName(false);
    }
  };

  const handleAddFeedback = async () => {
    const trimmed = feedbackText.trim();
    if (!trimmed) return;
    setSavingFeedback(true);
    setFeedbackError(null);
    try {
      const newFb = await contactFeedbackService.addFeedback(effectivePhone, trimmed);
      setFeedbacks(prev => [...prev, newFb]);
      setFeedbackText('');
    } catch (err) {
      console.error('[ContactDetailsDrawer] add feedback error:', err);
      setFeedbackError('Não foi possível salvar o comentário.');
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleRemoveFeedback = async (id) => {
    try {
      await contactFeedbackService.removeFeedback(effectivePhone, id);
      setFeedbacks(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      console.error('[ContactDetailsDrawer] remove feedback error:', err);
      setFeedbackError('Não foi possível remover.');
    }
  };

  const handleRemoveFollowup = async (followupId) => {
    if (!window.confirm('Cancelar este agendamento para este contato?')) return;
    setRemovingFollowupId(followupId);
    try {
      await followupService.removeRecipient(followupId, contactPhone);
      setFollowups(prev => prev.filter(f => f.id !== followupId));
    } catch (err) {
      alert('Erro ao cancelar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRemovingFollowupId(null);
    }
  };

  const activeFollowups = followups.filter(
    f => !(f.recurrence === 'once' && f.already_sent)
  );

  // Modo criar: abre a conversa para o número digitado (o pai fecha o drawer e
  // abre a tela de mensagem em onCreate).
  const checkDuplicate = async (digits) => {
    if (!createMode || !digits || digits.length < 10) { setDupInfo(null); return; }
    try {
      const res = await assignmentService.lookup(digits);
      const info = res?.data || null;
      // Erro transitório de lookup NÃO bloqueia: trata como "não existe".
      setDupInfo(info && info.exists ? info : null);
    } catch (err) {
      console.error('[ContactDetailsDrawer] lookup error:', err);
      setDupInfo(null);
    }
  };

  const handleCreate = async () => {
    // DDD+número sem código de país → assume BR (mesma regra do backend em
    // cadastrar_lead). Sem isso, "34 99653-7749" criava um contato keyed
    // "34996537749" — uma thread órfã que nunca casa com a conversa real.
    // normalizeCreatePhone é o MESMO helper do effectivePhone: a etiqueta
    // escolhida antes de salvar e o assignSelf gravam na mesma chave.
    const digits = normalizeCreatePhone(createPhone);
    if (onlyDigits(createPhone).length < 10) return;
    if (dupInfo?.exists && !forceCreate) return;   // duplicado: botão desabilitado
    // Reunião myCar 03/09: com o bloqueio ligado, o lead não nasce sem nome
    // de verdade (só telefone/número não vale).
    if (featureFlags?.required_fields_block) {
      const nm = (currentName || '').trim();
      if (!nm || !/[a-zA-ZÀ-ÿ]{2,}/.test(nm) || nm.replace(/\D/g, '').length >= 8) {
        setCreateNameError('Informe o nome do lead para criar o contato (não pode ser só o telefone).');
        return;
      }
    }
    if (createMissing.length) return;   // obrigatórios pendentes: aviso no rodapé
    setCreateNameError('');
    setCreating(true);
    try {
      // Atrela o responsável a quem está criando o lead (best-effort — não
      // bloqueia a criação se a conta não tiver company).
      try { await assignmentService.assignSelf(digits); }
      catch (e) { console.warn('[ContactDetailsDrawer] assignSelf:', e); }
      await onCreate?.(digits, (currentName || '').trim());
    } catch (err) {
      console.error('[ContactDetailsDrawer] create error:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (deleting || !contactPhone) return;
    setDeleting(true);
    try {
      await assignmentService.purgeContact(contactPhone);
      setShowDeleteConfirm(false);
      onDeleted?.(contactPhone);
      onClose?.();
    } catch (err) {
      console.error('[ContactDetailsDrawer] delete error:', err);
      alert('Erro ao excluir o contato: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleRekey = async () => {
    // Mesma regra do criar contato: sem isto "34 99199-7419" re-chaveava o
    // contato para 34991997419 (sem o 55), que a tela mostra como Espanha.
    if (onlyDigits(phoneDraft).length < 10 || savingPhone) return;
    const digits = normalizeCreatePhone(phoneDraft);
    setSavingPhone(true);
    try {
      await assignmentService.rekeyContact(contactPhone, digits);
      setShowRekeyConfirm(false);
      setEditingPhone(false);
      setPhoneDraft('');
      onRekeyed?.(digits);
      onClose?.();
    } catch (err) {
      console.error('[ContactDetailsDrawer] rekey error:', err);
      alert('Erro ao editar o número: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingPhone(false);
    }
  };

  return createPortal(
    <>
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1200,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420, maxWidth: '100%', height: '100%',
          backgroundColor: colors.bgPrimary,
          boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${colors.borderColor}`,
          backgroundColor: colors.headerBg,
        }}>
          <h3 style={{
            margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary,
          }}>
            {createMode ? 'Criar contato' : 'Dados do contato'}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: colors.textSecondary, padding: 6, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Avatar + name + phone block (centralizado, estilo WhatsApp) */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '28px 20px 20px',
            borderBottom: `8px solid ${colors.bgSecondary}`,
          }}>
            <div style={{
              width: 140, height: 140, borderRadius: '50%',
              overflow: 'hidden',
              backgroundColor: colors.bgTertiary,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: colors.textTertiary,
              marginBottom: 16,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}>
              {contactProfilePicUrl ? (
                <img
                  src={contactProfilePicUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <User size={64} />
              )}
            </div>

            {/* Nome editavel */}
            {editing ? (
              <div style={{ width: '100%', maxWidth: 320 }}>
                <input
                  autoFocus
                  type="text"
                  value={nameDraft}
                  disabled={savingName}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') {
                      setNameDraft(currentName || '');
                      setEditing(false);
                      setNameError(null);
                    }
                  }}
                  placeholder="Nome do contato"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: 18,
                    fontWeight: 600,
                    textAlign: 'center',
                    color: colors.textPrimary,
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.accentPrimary}60`,
                    borderRadius: 8,
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
                {nameError && (
                  <div style={{
                    fontSize: 12, color: '#DC2626', marginTop: 6,
                    textAlign: 'center',
                  }}>
                    {nameError}
                  </div>
                )}
                <div style={{
                  display: 'flex', justifyContent: 'center', gap: 8, marginTop: 10,
                }}>
                  <button
                    onClick={() => {
                      setNameDraft(currentName || '');
                      setEditing(false);
                      setNameError(null);
                    }}
                    disabled={savingName}
                    style={{
                      padding: '6px 14px',
                      background: 'transparent',
                      color: colors.textSecondary,
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: 8, cursor: 'pointer', fontSize: 13,
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveName}
                    disabled={savingName || !nameDraft.trim()}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 18px',
                      backgroundColor: colors.accentPrimary,
                      color: '#fff', border: 'none', borderRadius: 8,
                      cursor: savingName ? 'wait' : 'pointer',
                      fontSize: 13, fontWeight: 600,
                      opacity: !nameDraft.trim() ? 0.6 : 1,
                    }}
                  >
                    {savingName ? (
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Check size={14} />
                    )}
                    Salvar
                  </button>
                </div>
              </div>
            ) : isInstagram ? (
              // IG: nome vem do perfil do Instagram, sem edição local.
              <span style={{
                fontSize: 20, fontWeight: 600, color: colors.textPrimary,
                maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', padding: '4px 8px',
              }}>
                {displayName}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setNameDraft(nameIsPhone(currentName) ? '' : (currentName || ''));
                  setEditing(true);
                }}
                title="Editar nome"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'transparent', border: 'none',
                  cursor: 'pointer', padding: '4px 8px', borderRadius: 8,
                  color: colors.textPrimary,
                }}
              >
                <span style={{
                  fontSize: 20, fontWeight: 600, color: colors.textPrimary,
                  maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {displayName}
                </span>
                <Edit2 size={14} color={colors.textTertiary} />
              </button>
            )}

            {/* Telefone — editável no modo criar; exibição no modo normal */}
            {createMode ? (
              <>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
                  width: '100%', maxWidth: 320,
                  border: `1px solid ${colors.borderColor}`, borderRadius: 10,
                  padding: '0 10px', backgroundColor: colors.bgSecondary,
                }}>
                  <Phone size={14} color={colors.textTertiary} />
                  <input
                    autoFocus
                    type="tel"
                    inputMode="tel"
                    value={createPhone}
                    onChange={(e) => { setCreatePhone(e.target.value); setForceCreate(false); }}
                    onBlur={(e) => checkDuplicate(normalizeCreatePhone(e.target.value))}
                    placeholder="Telefone com DDD"
                    style={{
                      flex: 1, border: 'none', outline: 'none', background: 'transparent',
                      color: colors.textPrimary, fontSize: 14, padding: '11px 0',
                      fontFamily: 'inherit', textAlign: 'center',
                    }}
                  />
                </div>
                {/* Com "+" o país digitado é respeitado; sem "+" vale DDD brasileiro. */}
                <span style={{ marginTop: 6, fontSize: 11.5, color: colors.textTertiary }}>
                  Número de outro país: comece com +
                </span>
              </>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                marginTop: 6,
                fontSize: 13, color: colors.textSecondary,
              }}>
                <Phone size={13} />
                {editingPhone ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      autoFocus
                      type="tel"
                      inputMode="tel"
                      value={phoneDraft}
                      onChange={(e) => setPhoneDraft(e.target.value)}
                      placeholder="Novo número com DDD"
                      style={{
                        border: `1px solid ${colors.borderColor}`, borderRadius: 6,
                        padding: '4px 8px', fontSize: 13, background: colors.bgPrimary,
                        color: colors.textPrimary, outline: 'none', width: 150,
                      }}
                    />
                    <button
                      onClick={() => setShowRekeyConfirm(true)}
                      disabled={onlyDigits(phoneDraft).length < 10}
                      title="Salvar número"
                      style={{
                        background: 'transparent', border: 'none', padding: 2,
                        cursor: onlyDigits(phoneDraft).length < 10 ? 'not-allowed' : 'pointer',
                        color: colors.accentPrimary, opacity: onlyDigits(phoneDraft).length < 10 ? 0.4 : 1,
                      }}
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={() => { setEditingPhone(false); setPhoneDraft(''); }}
                      title="Cancelar"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colors.textTertiary, padding: 2 }}
                    >
                      <X size={16} />
                    </button>
                  </span>
                ) : isInstagram ? (
                  // IG: sem telefone. Mostra o @handle (se veio) e nada de
                  // editar-número / vincular-números (ambos telefone-cêntricos).
                  contactSubtitle
                    ? <span style={{ color: colors.textSecondary }}>{contactSubtitle}</span>
                    : null
                ) : (
                  <>
                    {contactPhone ? `+${String(contactPhone).replace(/\D/g, '')}` : ''}
                    {isCompanyAdmin && contactPhone && (
                      <button
                        onClick={() => { setPhoneDraft(''); setEditingPhone(true); }}
                        title="Editar número"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colors.textTertiary, padding: 2 }}
                      >
                        <Edit2 size={13} />
                      </button>
                    )}
                    {featureFlags.lead_model && contactPhone && (
                      <button
                        onClick={() => setShowLinkNumbers(true)}
                        title="Números do contato (vincular outro número)"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colors.textTertiary, padding: 2 }}
                      >
                        <Users size={13} />
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* P5 — Mesma pessoa: outras identidades do lead (referencia passiva;
              conversas seguem SEPARADAS). Channel-agnostic -> Instagram Direct
              entra automatico quando o canal existir. Junto, o vínculo entre
              canais gravado pelo cadastro do agente (linked_contacts), com
              link para abrir a conversa do outro canal. */}
          {/* Lead unificado nos 3 canais (flag lead_multichannel): chips por
              canal com botao para abrir a conversa do outro canal e, para o
              admin, "Vincular canal". Substitui o texto "Também é contato em". */}
          {!createMode && featureFlags.lead_multichannel && (
            <ContactChannelChips
              variant="drawer"
              contactKey={contactPhone}
              channel={effectiveChannel}
              colors={colors}
              links={linkedContacts}
              reloadKey={linkedReload}
              onLink={isCompanyAdmin ? () => setShowLinkChannel(true) : undefined}
              onBeforeNavigate={onClose}
              defaultChannel={user?.default_channel}
            />
          )}
          {!createMode && ((featureFlags.lead_model && leadOthers.length > 0) || (linkedContacts.length > 0 && !featureFlags.lead_multichannel)) && (
            <div style={{
              margin: '10px 20px 0', padding: '8px 12px', borderRadius: 10,
              backgroundColor: colors.bgSecondary,
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: colors.textTertiary,
                textTransform: 'uppercase', letterSpacing: '0.4px',
              }}>
                Mesma pessoa
              </span>
              {featureFlags.lead_model && leadOthers.length > 0 && (
                <span style={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: '18px' }}>
                  Também conversa em {leadOthers.map(formatIdentity).join(' · ')}
                </span>
              )}
              {linkedContacts.length > 0 && !featureFlags.lead_multichannel && (
                <span style={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: '18px' }}>
                  Também é contato em:{' '}
                  {linkedContacts.map((link, i) => {
                    const alvo = linkedContactTarget(link, user?.default_channel);
                    const rotulo = linkedContactLabel(link);
                    return (
                      <React.Fragment key={link.key || i}>
                        {i > 0 && ' · '}
                        {alvo ? (
                          <button
                            type="button"
                            title="Abrir conversa"
                            onClick={() => {
                              onClose?.();
                              navigate(alvo.route, { state: alvo.state });
                            }}
                            style={{
                              background: 'transparent', border: 'none', padding: 0,
                              cursor: 'pointer', color: colors.accentPrimary,
                              fontSize: 'inherit', fontWeight: 600, fontFamily: 'inherit',
                            }}
                          >
                            {rotulo}
                          </button>
                        ) : rotulo}
                      </React.Fragment>
                    );
                  })}
                </span>
              )}
            </div>
          )}

          {/* Criar-lead: aviso de duplicado (lead já existe na base) */}
          {createMode && dupInfo?.exists && !forceCreate && (
            <section style={{ padding: '12px 20px', borderBottom: `8px solid ${colors.bgSecondary}` }}>
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 10,
                backgroundColor: 'rgba(245, 158, 11, 0.10)',
                border: '1px solid rgba(245, 158, 11, 0.35)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle size={16} color="#d97706" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                    Este lead já está cadastrado
                  </span>
                </div>
                <span style={{ fontSize: 12, color: colors.textSecondary }}>
                  Responsável:{' '}
                  {dupInfo.responsible
                    ? `${dupInfo.responsible.name || 'Agente IA'}${dupInfo.responsible.type === 'agent' ? ' (agente)' : ''}`
                    : 'sem responsável definido'}
                </span>
                <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                  <button
                    type="button"
                    // canonical_phone: o número REAL da thread encontrada. O
                    // digitado pode vir sem o DDI 55 ou com o 9º dígito
                    // divergente — abrir por ele caía numa conversa vazia
                    // (caso Santos, 12/08).
                    onClick={() => onCreate?.(
                      dupInfo.canonical_phone || effectivePhone,
                      (dupInfo.contact?.name || currentName || '').trim()
                    )}
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, backgroundColor: colors.accentPrimary, color: '#fff',
                    }}
                  >
                    Abrir conversa
                  </button>
                  <button
                    type="button"
                    onClick={() => setForceCreate(true)}
                    style={{
                      padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                      backgroundColor: 'transparent', color: colors.textSecondary,
                      border: `1px solid ${colors.borderColor}`,
                    }}
                  >
                    Criar mesmo assim
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* 1) Responsável — responsável atual + origem de entrada + histórico
              de transferências (paginado 2 a 2). Só no modo edição. */}
          {!createMode && (
            <CollapsibleSection
              title="Responsável"
              icon={<UserCheck size={16} color={colors.accentPrimary} />}
              colors={colors}
              resetKey={contactPhone}
              defaultOpen
            >
              {loadingAssignment ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  color: colors.textSecondary, fontSize: 12,
                }}>
                  <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                  Carregando…
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Responsável atual
                    </span>
                    {assignmentInfo ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        alignSelf: 'flex-start',
                        backgroundColor: 'rgba(245,158,11,0.14)', color: '#b45309',
                        fontSize: 12, fontWeight: 600, padding: '4px 10px',
                        borderRadius: 999,
                      }}>
                        {assignmentInfo.type === 'agent' ? <Bot size={12} /> : <User size={12} />}
                        {assignmentInfo.name}
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: colors.textSecondary, fontStyle: 'italic' }}>
                        Sem responsável definido
                      </span>
                    )}
                  </div>

                  {originInfo && (originInfo.origin_number || originInfo.origin_member_name) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        Chegou pelo número
                      </span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        alignSelf: 'flex-start',
                        backgroundColor: 'rgba(37,99,235,0.12)', color: '#1d4ed8',
                        fontSize: 12, fontWeight: 600, padding: '4px 10px',
                        borderRadius: 999,
                      }}>
                        <Phone size={12} />
                        {originInfo.origin_number
                          || (originInfo.channel === 'wa_business' ? 'WhatsApp Business' : 'WhatsApp')}
                        {originInfo.origin_member_name
                          ? ` · ${originInfo.origin_member_name}${originInfo.origin_is_admin ? ' (admin)' : ''}`
                          : ''}
                      </span>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Histórico de transferências
                    </span>
                    {assignmentHistory.length === 0 ? (
                      <span style={{ fontSize: 12, color: colors.textSecondary, fontStyle: 'italic' }}>
                        Nenhuma transferência registrada.
                      </span>
                    ) : (
                      <>
                        {assignmentHistory.slice(0, historyVisible).map((ev) => (
                          <div key={ev.id} style={{
                            display: 'flex', flexDirection: 'column', gap: 2,
                            padding: '8px 10px', borderRadius: 8,
                            backgroundColor: colors.bgSecondary,
                          }}>
                            <span style={{ fontSize: 12, color: colors.textPrimary }}>
                              {ev.event_type === 'cleared'
                                ? `Responsável removido${ev.prev?.name ? ` (era ${ev.prev.name})` : ''}`
                                : `${ev.prev?.name ? `${ev.prev.name} → ` : ''}${ev.next?.name || ''}`}
                            </span>
                            <span style={{ fontSize: 11, color: colors.textTertiary }}>
                              {formatFeedbackDate(ev.timestamp)}
                              {ev.actor?.name ? ` · por ${ev.actor.name}` : ''}
                              {ev.reason ? ` · ${ev.reason}` : ''}
                            </span>
                          </div>
                        ))}
                        {assignmentHistory.length > historyVisible && (
                          <button
                            type="button"
                            onClick={() => setHistoryVisible((v) => v + 2)}
                            style={{
                              alignSelf: 'flex-start', marginTop: 2,
                              background: 'transparent', border: 'none', cursor: 'pointer',
                              color: colors.accentPrimary, fontSize: 12, fontWeight: 600, padding: '2px 0',
                            }}
                          >
                            Ver mais ({assignmentHistory.length - historyVisible})
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* 1b) Histórico do funil — auditoria de entrada/saída de etapa
              (eventos que o backend já grava). Paginado 2 a 2. */}
          {!createMode && (
            <CollapsibleSection
              title="Histórico do funil"
              icon={<GitBranch size={16} color={colors.accentPrimary} />}
              count={funnelHistory.length}
              colors={colors}
              resetKey={contactPhone}
            >
              {funnelHistory.length === 0 ? (
                <span style={{ fontSize: 12, color: colors.textSecondary, fontStyle: 'italic' }}>
                  Nenhuma mudança de funil registrada.
                </span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {funnelHistory.slice(0, funnelHistVisible).map((ev) => (
                    <div key={ev.id} style={{
                      display: 'flex', flexDirection: 'column', gap: 2,
                      padding: '8px 10px', borderRadius: 8,
                      backgroundColor: colors.bgSecondary,
                    }}>
                      <span style={{ fontSize: 12, color: colors.textPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          backgroundColor: ev.label_color || colors.accentPrimary,
                        }} />
                        {ev.event_type === 'assigned' ? 'Entrou em ' : 'Saiu de '}
                        <strong>{ev.label_name || '(etapa removida)'}</strong>
                        {ev.pipeline_name ? ` · funil ${ev.pipeline_name}` : ''}
                      </span>
                      <span style={{ fontSize: 11, color: colors.textTertiary }}>
                        {formatFeedbackDate(ev.timestamp)}
                        {FUNNEL_EVENT_SOURCES[ev.source] ? ` · via ${FUNNEL_EVENT_SOURCES[ev.source]}` : ''}
                      </span>
                      {/* Motivo do fechamento, quando o operador preencheu ao
                          mover para uma etapa de ganho/perda. */}
                      {ev.outcome_reason && (
                        <span style={{
                          marginTop: 3, fontSize: 11, lineHeight: 1.45,
                          color: colors.textSecondary, paddingLeft: 8,
                          borderLeft: `2px solid ${
                            ev.outcome === 'won' ? colors.accentPrimary : colors.danger
                          }`,
                        }}>
                          <strong style={{ color: colors.textPrimary }}>
                            {ev.outcome === 'won' ? 'Motivo do ganho' : 'Motivo da perda'}:
                          </strong>{' '}
                          {ev.outcome_reason}
                        </span>
                      )}
                    </div>
                  ))}
                  {funnelHistory.length > funnelHistVisible && (
                    <button
                      type="button"
                      onClick={() => setFunnelHistVisible((v) => v + 2)}
                      style={{
                        alignSelf: 'flex-start', marginTop: 2,
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: colors.accentPrimary, fontSize: 12, fontWeight: 600, padding: '2px 0',
                      }}
                    >
                      Ver mais ({funnelHistory.length - funnelHistVisible})
                    </button>
                  )}
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* 1c) Agendamentos do contato — eventos nativos do calendário
              vinculados a este lead (auditoria de test drive, avaliação etc.). */}
          {!createMode && (
            <CollapsibleSection
              title="Agendamentos"
              icon={<Calendar size={16} color={colors.accentPrimary} />}
              count={contactEvents.length}
              colors={colors}
              resetKey={contactPhone}
            >
              {contactEvents.length === 0 ? (
                <span style={{ fontSize: 12, color: colors.textSecondary, fontStyle: 'italic' }}>
                  Nenhum agendamento vinculado a este contato.
                </span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {contactEvents.slice(0, eventsVisible).map((ev) => (
                    <div key={ev.id} style={{
                      display: 'flex', flexDirection: 'column', gap: 2,
                      padding: '8px 10px', borderRadius: 8,
                      backgroundColor: colors.bgSecondary,
                    }}>
                      <span style={{ fontSize: 12, color: colors.textPrimary, fontWeight: 600 }}>
                        {ev.title || 'Evento'}
                        {ev.event_type?.name ? ` · ${ev.event_type.name}` : ''}
                      </span>
                      <span style={{ fontSize: 11, color: colors.textTertiary }}>
                        {formatFeedbackDate(ev.start_at)}
                        {ev.responsible?.name ? ` · ${ev.responsible.name}` : ''}
                      </span>
                    </div>
                  ))}
                  {contactEvents.length > eventsVisible && (
                    <button
                      type="button"
                      onClick={() => setEventsVisible((v) => v + 2)}
                      style={{
                        alignSelf: 'flex-start', marginTop: 2,
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: colors.accentPrimary, fontSize: 12, fontWeight: 600, padding: '2px 0',
                      }}
                    >
                      Ver mais ({contactEvents.length - eventsVisible})
                    </button>
                  )}
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* 2) Tags do contato — tags livres (conceito distinto de funil). */}
          <CollapsibleSection
            title="Tags do contato"
            icon={<Tag size={16} color={colors.accentPrimary} />}
            count={tags.length}
            colors={colors}
            resetKey={contactPhone}
            defaultOpen
          >
            <p style={{ margin: '0 0 10px', fontSize: 12, color: colors.textSecondary, lineHeight: 1.4 }}>
              Marque este contato com tags livres. Digite para buscar uma existente
              ou criar uma nova na hora.
            </p>
            <TagCombobox
              contactPhone={effectivePhone}
              colors={colors}
              onChange={setTags}
            />
          </CollapsibleSection>

          {/* 3) Funil / etapa — ver/mudar em qual etapa do funil o contato está.
              Mesma seção nos dois modos (criar-lead coloca direto numa etapa). */}
          <CollapsibleSection
            title="Funil / etapa"
            icon={<GitBranch size={16} color={colors.accentPrimary} />}
            colors={colors}
            resetKey={contactPhone}
            defaultOpen
          >
            <p style={{ margin: '0 0 10px', fontSize: 12, color: colors.textSecondary, lineHeight: 1.4 }}>
              {createMode
                ? `Coloque o lead direto numa etapa do funil.${canPickFunnel ? '' : ' Digite o telefone completo primeiro.'}`
                : 'Toque na etapa para mudar o contato de funil.'}
            </p>
            {multiFunnel && !createMode && contactFunnel.entries.length > 0 ? (
              /* Multi-funil: uma tag por funil, cada uma com "Sair deste
                 funil"; "Entrar em outro funil" abre o mesmo seletor. */
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {contactFunnel.entries.map(({ label: st, funnelName: fn }) => (
                  <span
                    key={st.id}
                    title="Clique para mudar a etapa neste funil"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      maxWidth: '100%', padding: '6px 10px', borderRadius: 999,
                      backgroundColor: `${st.color || colors.accentPrimary}18`,
                      border: `1px solid ${st.color || colors.accentPrimary}55`,
                    }}
                  >
                    <span
                      onClick={() => setLabelModalOpen(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', minWidth: 0 }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: st.color || colors.accentPrimary, flexShrink: 0 }} />
                      <span style={{
                        fontSize: 13, fontWeight: 600, color: colors.textPrimary,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {fn ? `${fn} · ${st.name}` : st.name}
                      </span>
                      <Edit2 size={12} color={colors.textTertiary} style={{ flexShrink: 0 }} />
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleLeaveFunnel(st.id); }}
                      disabled={leavingFunnelLabelId === st.id}
                      title={`Sair do funil ${fn || ''}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: 'transparent', border: 'none', padding: 0,
                        cursor: leavingFunnelLabelId === st.id ? 'wait' : 'pointer',
                        color: colors.textTertiary, flexShrink: 0,
                      }}
                    >
                      {leavingFunnelLabelId === st.id
                        ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        : <X size={12} />}
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => setLabelModalOpen(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                    borderRadius: 999, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    backgroundColor: 'transparent', color: colors.textSecondary,
                    border: `1px dashed ${colors.borderColor}`,
                  }}
                >
                  <GitBranch size={12} /> Entrar em outro funil
                </button>
              </div>
            ) : contactFunnel.hasLabel && contactFunnel.funnelName ? (
              /* Tag "Funil · Etapa" atual — clicável, abre o seletor. */
              <span
                onClick={() => setLabelModalOpen(true)}
                title="Clique para alterar o funil/etapa"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  maxWidth: '100%', padding: '6px 12px', borderRadius: 999,
                  cursor: 'pointer',
                  backgroundColor: `${contactFunnel.color || colors.accentPrimary}18`,
                  border: `1px solid ${contactFunnel.color || colors.accentPrimary}55`,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: contactFunnel.color || colors.accentPrimary, flexShrink: 0 }} />
                <span style={{
                  fontSize: 13, fontWeight: 600, color: colors.textPrimary,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {contactFunnel.funnelName}{contactFunnel.stageName ? ` · ${contactFunnel.stageName}` : ''}
                </span>
                <Edit2 size={12} color={colors.textTertiary} style={{ flexShrink: 0 }} />
              </span>
            ) : (
              <button
                type="button"
                disabled={!canPickFunnel}
                onClick={() => setLabelModalOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 8,
                  cursor: canPickFunnel ? 'pointer' : 'not-allowed',
                  backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                  border: `1px solid ${colors.borderColor}`, fontSize: 13,
                  opacity: canPickFunnel ? 1 : 0.5,
                }}
              >
                <GitBranch size={14} /> Definir etapa
              </button>
            )}

            {/* Último fechamento registrado neste contato. Fica à vista aqui
                porque o "Histórico do funil" nasce recolhido e paginado — o
                operador precisa saber de relance se o lead já foi ganho ou
                perdido, e por quê. */}
            {!createMode && lastOutcome && (
              <div style={{
                marginTop: 10, padding: '8px 10px', borderRadius: 8,
                backgroundColor: colors.bgSecondary,
                borderLeft: `3px solid ${
                  lastOutcome.outcome === 'won' ? colors.accentPrimary : colors.danger
                }`,
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 600,
                  color: lastOutcome.outcome === 'won' ? colors.accentPrimary : colors.danger,
                }}>
                  {lastOutcome.outcome === 'won' ? 'Ganho' : 'Perda'}
                  {lastOutcome.label_name ? ` · ${lastOutcome.label_name}` : ''}
                </div>
                {lastOutcome.outcome_reason && (
                  <div style={{ marginTop: 3, fontSize: 12, lineHeight: 1.45, color: colors.textPrimary }}>
                    {lastOutcome.outcome_reason}
                  </div>
                )}
                <div style={{ marginTop: 3, fontSize: 11, color: colors.textTertiary }}>
                  {formatFeedbackDate(lastOutcome.timestamp)}
                  {lastOutcome.outcome_reason ? '' : ' · sem motivo registrado'}
                </div>
              </div>
            )}
          </CollapsibleSection>

          {/* 4) Campos personalizados (abas estilo Kommo) */}
          <CollapsibleSection
            title="Campos personalizados"
            icon={<SlidersHorizontal size={16} color={colors.accentPrimary} />}
            colors={colors}
            resetKey={contactPhone}
            defaultOpen
          >
            <CustomFieldsPanel
              colors={colors}
              phone={effectivePhone}
              compact
              hideHeader
              onValuesChange={requireOnCreate
                ? (values, definitions) => setCreateFields({ values, definitions })
                : undefined}
            />
          </CollapsibleSection>

          {/* 5) Comentários */}
          <CollapsibleSection
            title="Comentários"
            icon={<MessageSquarePlus size={16} color={colors.accentPrimary} />}
            count={feedbacks.length}
            colors={colors}
            resetKey={contactPhone}
            defaultOpen
          >
            {loadingFeedbacks ? (
              <div style={{
                display: 'flex', justifyContent: 'center', padding: 16,
                color: colors.textSecondary, fontSize: 12, gap: 8,
                alignItems: 'center',
              }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Carregando…
              </div>
            ) : feedbacks.length === 0 ? (
              <div style={{
                fontSize: 12, fontStyle: 'italic',
                color: colors.textTertiary, textAlign: 'center', padding: 8,
              }}>
                Nenhum comentário cadastrado.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {feedbacks.map(fb => (
                  <div key={fb.id} style={{
                    padding: 10,
                    borderRadius: 8,
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.borderColor}`,
                    display: 'flex', gap: 8, alignItems: 'flex-start',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 12, color: colors.textPrimary,
                        lineHeight: 1.4, whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}>
                        {fb.text}
                      </div>
                      <div style={{
                        fontSize: 10, color: colors.textTertiary, marginTop: 4,
                      }}>
                        {formatFeedbackDate(fb.created_at)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFeedback(fb.id)}
                      title="Remover"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: '#DC2626', padding: 4, borderRadius: 6, flexShrink: 0,
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Composer feedback */}
            <div style={{ marginTop: 10 }}>
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Adicionar comentário sobre este contato…"
                rows={2}
                disabled={savingFeedback}
                style={{
                  width: '100%', padding: 8, fontSize: 12,
                  backgroundColor: colors.bgPrimary,
                  color: colors.textPrimary,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 8, resize: 'vertical', fontFamily: 'inherit',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              {feedbackError && (
                <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                  {feedbackError}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                <button
                  onClick={handleAddFeedback}
                  disabled={savingFeedback || !feedbackText.trim()}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 18px', fontSize: 13, fontWeight: 600,
                    backgroundColor: colors.accentPrimary,
                    color: '#fff', border: 'none', borderRadius: 8,
                    cursor: !feedbackText.trim() || savingFeedback
                      ? 'not-allowed' : 'pointer',
                    opacity: !feedbackText.trim() || savingFeedback ? 0.5 : 1,
                  }}
                >
                  {savingFeedback && (
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  )}
                  Adicionar
                </button>
              </div>
            </div>
          </CollapsibleSection>

          {/* 6) Follow-ups agendados — só no modo edição. */}
          {!createMode && (
          <CollapsibleSection
            title="Follow-ups agendados"
            icon={<Calendar size={16} color={colors.accentPrimary} />}
            count={activeFollowups.length}
            colors={colors}
            resetKey={contactPhone}
            defaultOpen
          >

            {loadingFollowups ? (
              <div style={{
                display: 'flex', justifyContent: 'center', padding: 16,
                color: colors.textSecondary, fontSize: 12, gap: 8,
                alignItems: 'center',
              }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Carregando…
              </div>
            ) : activeFollowups.length === 0 ? (
              <div style={{
                fontSize: 12, fontStyle: 'italic',
                color: colors.textTertiary, textAlign: 'center', padding: 8,
              }}>
                Nenhum followup ativo para este contato.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {activeFollowups.map(fu => (
                  <div key={fu.id} style={{
                    padding: 10, borderRadius: 8,
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.borderColor}`,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: 500, color: colors.textPrimary,
                      marginBottom: 4,
                    }}>
                      {fu.name || 'Sem nome'}
                    </div>
                    <div style={{
                      display: 'flex', flexDirection: 'column', gap: 2,
                      marginBottom: 8,
                    }}>
                      <span style={{
                        fontSize: 11, color: colors.textSecondary,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        <Repeat size={10} /> {buildScheduleText(fu.schedule)}
                      </span>
                      <span style={{
                        fontSize: 11, color: colors.textSecondary,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        <Clock size={10} />
                        {fu.message_count || 0} mensagen
                        {(fu.message_count || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => handleRemoveFollowup(fu.id)}
                        disabled={removingFollowupId === fu.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          padding: '4px 10px', borderRadius: 6, fontSize: 11,
                          background: 'none', border: '1px solid #ef444430',
                          color: '#ef4444', cursor: 'pointer',
                          opacity: removingFollowupId === fu.id ? 0.5 : 1,
                        }}
                      >
                        {removingFollowupId === fu.id ? (
                          <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Trash2 size={10} />
                        )}
                        Cancelar
                      </button>
                      {onNavigateToFollowup && (
                        <button
                          onClick={() => {
                            onNavigateToFollowup(fu.id);
                            onClose?.();
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '4px 10px', borderRadius: 6, fontSize: 11,
                            background: 'none',
                            border: `1px solid ${colors.borderColor}`,
                            color: colors.textSecondary, cursor: 'pointer',
                          }}
                        >
                          <ExternalLink size={10} /> Ver detalhes
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
          )}
          {!createMode && (
            <div style={{ marginTop: 8 }}>
              <BlocklistToggle contactKey={contactPhone} colors={colors} />
            </div>
          )}
        </div>
        {createMode && (
          <div style={{
            padding: '14px 20px', borderTop: `1px solid ${colors.borderColor}`, flexShrink: 0,
          }}>
            {createNameError && (
              <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8 }}>{createNameError}</div>
            )}
            {createMissing.length > 0 && (
              <div style={{ fontSize: 12, color: '#B45309', marginBottom: 8, lineHeight: 1.4 }}>
                Preencha os campos obrigatórios: {createMissing.map((d) => d.name).join(', ')}
              </div>
            )}
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating || onlyDigits(createPhone).length < 10 || (dupInfo?.exists && !forceCreate) || createMissing.length > 0}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '12px 18px', borderRadius: 10, border: 'none',
                backgroundColor: colors.accentPrimary, color: '#fff',
                fontSize: 14, fontWeight: 600, cursor: creating ? 'wait' : 'pointer',
                opacity: (creating || onlyDigits(createPhone).length < 10 || (dupInfo?.exists && !forceCreate) || createMissing.length > 0) ? 0.6 : 1,
              }}
            >
              {creating && <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />}
              Criar contato
            </button>
          </div>
        )}
        {/* Excluir vale para TODOS os canais: a purga agora casa o contato pela
            chave do canal (telefone ou 'instagram:'/'messenger:'), então o
            botão não precisa mais ficar fora do IG/Messenger. */}
        {!createMode && isCompanyAdmin && (
          <div style={{ padding: '14px 20px', borderTop: `1px solid ${colors.borderColor}`, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '11px 18px', borderRadius: 10,
                border: `1px solid ${colors.borderColor}`, background: 'transparent',
                color: '#DC2626', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <Trash2 size={15} /> Excluir contato
            </button>
          </div>
        )}
      </div>
    </div>

    <ContactTagsManagerModal
      isOpen={showTagsModal}
      onClose={() => setShowTagsModal(false)}
      contactPhone={contactPhone}
      contactName={currentName || contactPhone}
      colors={colors}
      displayMode="drawer"
      zIndex={1300}
      onChange={(finalTags) => { setTags(finalTags); }}
    />

    {showLinkChannel && (
      <LinkChannelModal
        isOpen={showLinkChannel}
        onClose={() => setShowLinkChannel(false)}
        contactKey={contactPhone}
        contactName={currentName || contactPhone}
        colors={colors}
        zIndex={1300}
        onLinked={() => {
          // Recarrega o responsavel, a etapa e os vinculos: o merge pode ter
          // trazido tudo isso do outro cc.
          setLinkedReload((v) => v + 1);
          loadAssignment();
          contactFunnel?.reload?.();
        }}
      />
    )}

    {showLinkNumbers && (
      <LinkNumbersModal
        isOpen={showLinkNumbers}
        onClose={() => setShowLinkNumbers(false)}
        contactPhone={contactPhone}
        contactName={currentName || contactPhone}
        colors={colors}
        channel={source}
        zIndex={1300}
      />
    )}

    {labelModalOpen && (
      <LabelPickerModal
        isOpen={labelModalOpen}
        onClose={() => { setLabelModalOpen(false); contactFunnel.reload(); }}
        contactPhone={effectivePhone}
        contactName={(currentName || '').trim()}
        colors={colors}
        displayMode="drawer"
        zIndex={1300}
        onChange={() => {
          contactFunnel.reload();  // tag "Funil · Etapa" atualiza na hora
        }}
      />
    )}

    {showDeleteConfirm && (
      <div
        onClick={() => !deleting && setShowDeleteConfirm(false)}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
          zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{
          width: 400, maxWidth: '100%', backgroundColor: colors.bgPrimary,
          borderRadius: 14, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <AlertCircle size={20} color="#DC2626" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
              Excluir contato
            </h3>
          </div>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
            Isso apaga <b>de vez</b> {currentName || contactPhone} — a conversa nos dois
            canais, mensagens, followups e a conversa com a IA. <b>Não dá para desfazer.</b>
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
              style={{
                padding: '9px 16px', borderRadius: 8, border: `1px solid ${colors.borderColor}`,
                background: 'transparent', color: colors.textPrimary, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 8,
                border: 'none', backgroundColor: '#DC2626', color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: deleting ? 'wait' : 'pointer', opacity: deleting ? 0.7 : 1,
              }}
            >
              {deleting && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
              Excluir de vez
            </button>
          </div>
        </div>
      </div>
    )}

    {showRekeyConfirm && (
      <div
        onClick={() => !savingPhone && setShowRekeyConfirm(false)}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
          zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{
          width: 400, maxWidth: '100%', backgroundColor: colors.bgPrimary,
          borderRadius: 14, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
            Editar número
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
            Mover {currentName || contactPhone} para <b>+{onlyDigits(phoneDraft)}</b>? O contato e
            todo o histórico vão junto. Se o novo número já existir, os dois serão <b>mesclados</b>.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowRekeyConfirm(false)}
              disabled={savingPhone}
              style={{
                padding: '9px 16px', borderRadius: 8, border: `1px solid ${colors.borderColor}`,
                background: 'transparent', color: colors.textPrimary, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleRekey}
              disabled={savingPhone}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 8,
                border: 'none', backgroundColor: colors.accentPrimary, color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: savingPhone ? 'wait' : 'pointer', opacity: savingPhone ? 0.7 : 1,
              }}
            >
              {savingPhone && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
              Mover número
            </button>
          </div>
        </div>
      </div>
    )}
    </>,
    (typeof document !== 'undefined' && (document.getElementById('modal-root') || document.body)) || document.body
  );
};

export default ContactDetailsDrawer;
